import {useCallback, useEffect, useState} from 'react';

import {Alert, Box, Button, MenuItem, Stack, TextField, Typography} from '@mui/material';

import {API_CONFIG} from '../contentGenerator/config';
import {getAppToken, getStorePathPrefix, onUnauthorized, setAppToken} from './apiFetch';

const STATUS_URL = `${API_CONFIG.PROXY_URL}/auth/status`;
const LOGIN_URL = `${API_CONFIG.PROXY_URL}/auth/login`;
const SETUP_URL = `${API_CONFIG.PROXY_URL}/auth/setup`;
const LOGOUT_URL = `${API_CONFIG.PROXY_URL}/auth/logout`;
const STORES_URL = `${API_CONFIG.PROXY_URL}/stores`;

const STATE = {
    CHECKING: 'checking',
    AUTHED: 'authed',
    LOGIN: 'login',
    SETUP: 'setup',
};

// If the page is loaded at /s/<slug>/, the store is pinned by the URL —
// derive its myshopify.com domain so the login form targets it without
// a picker.
function storeFromPathPrefix() {
    const prefix = getStorePathPrefix();
    if (!prefix) return '';
    const slug = prefix.replace(/^\/s\//, '');
    return slug ? `${slug}.myshopify.com` : '';
}

async function parseApiResponse(response, fallbackMessage) {
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) {
        const error = new Error(
            payload?.error ||
                payload?.message ||
                (typeof payload === 'string' && payload) ||
                fallbackMessage,
        );
        error.payload = payload;
        error.status = response.status;
        throw error;
    }

    return payload;
}

// Status + store list both take an explicit store so the form can show
// the right login-vs-setup state for whichever store is selected.
async function probeStatus(store) {
    const headers = {};
    const token = getAppToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (store) headers['X-Active-Store'] = store;

    const response = await fetch(STATUS_URL, {headers});
    return parseApiResponse(response, `Status probe failed (${response.status})`);
}

async function fetchStores() {
    try {
        const response = await fetch(STORES_URL);
        const payload = await parseApiResponse(response, 'Could not load stores.');
        return {
            stores: Array.isArray(payload?.stores) ? payload.stores : [],
            activeStore: payload?.activeStore || '',
        };
    } catch {
        return {stores: [], activeStore: ''};
    }
}

export default function AppAuthGate({children}) {
    const [state, setState] = useState(STATE.CHECKING);
    const [error, setError] = useState('');
    const [credentials, setCredentials] = useState({username: '', password: ''});
    const [submitting, setSubmitting] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState('');
    // When the URL carries /s/<slug>/, the store is fixed and the picker
    // is hidden — the user committed to a store by navigating there.
    const [storeLockedByPath] = useState(() => Boolean(storeFromPathPrefix()));

    const resolveStateForStore = useCallback(async (store) => {
        try {
            const status = await probeStatus(store);
            if (status.setupRequired) {
                setAppToken('');
                setCurrentUser(null);
                setState(STATE.SETUP);
                return;
            }
            if (!status.appAuthRequired || status.appAuthorized) {
                setCurrentUser(status.user || null);
                setState(STATE.AUTHED);
                return;
            }
            setAppToken('');
            setCurrentUser(null);
            setState(STATE.LOGIN);
        } catch (err) {
            setError(err.message || 'Could not reach the server.');
            setAppToken('');
            setCurrentUser(null);
            setState(STATE.LOGIN);
        }
    }, []);

    // Bootstrap: load the store list, pick a sensible default, then
    // resolve login/setup/authed state for that store.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const pathStore = storeFromPathPrefix();
            const {stores: loadedStores, activeStore} = await fetchStores();
            if (cancelled) return;

            setStores(loadedStores);
            const defaultStore =
                pathStore ||
                (loadedStores.some((s) => s.store === activeStore) ? activeStore : '') ||
                loadedStores[0]?.store ||
                '';
            setSelectedStore(defaultStore);
            await resolveStateForStore(defaultStore);
        })();
        return () => {
            cancelled = true;
        };
    }, [resolveStateForStore]);

    useEffect(() => {
        onUnauthorized(() => {
            setAppToken('');
            setCurrentUser(null);
            setState(STATE.LOGIN);
            setError('Session expired. Sign in again.');
        });
        return () => onUnauthorized(null);
    }, []);

    const updateCredential = (field) => (event) => {
        setCredentials((current) => ({...current, [field]: event.target.value}));
    };

    const handleStoreChange = async (event) => {
        const nextStore = event.target.value;
        setSelectedStore(nextStore);
        setError('');
        // Re-probe so the form flips between "sign in" and "create first
        // user" depending on whether the newly selected store has users.
        setState(STATE.CHECKING);
        await resolveStateForStore(nextStore);
    };

    const completeLogin = (payload) => {
        setAppToken(payload.token || '');
        setCurrentUser(payload.user || null);
        setCredentials({username: '', password: ''});
        setState(STATE.AUTHED);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const username = credentials.username.trim();
        const password = credentials.password;
        if (!username || !password) {
            setError('Username and password are required.');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const body = {username, password};
            if (selectedStore) body.store = selectedStore;

            const response = await fetch(state === STATE.SETUP ? SETUP_URL : LOGIN_URL, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(body),
            });
            const payload = await parseApiResponse(response, `Login failed (${response.status})`);
            completeLogin(payload);
        } catch (err) {
            // A login against a store with no users yet returns 409 +
            // setupRequired — flip to the setup form so the user can
            // create the first account for that store in one step.
            if (err.status === 409 && err.payload?.setupRequired) {
                setState(STATE.SETUP);
                setError('This store has no users yet — create the first account.');
            } else {
                setError(err.message || 'Login failed.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogout = useCallback(async () => {
        const token = getAppToken();
        setAppToken('');
        setCurrentUser(null);
        setState(STATE.LOGIN);
        setError('');

        if (!token) return;

        try {
            await fetch(LOGOUT_URL, {
                method: 'POST',
                headers: {Authorization: `Bearer ${token}`},
            });
        } catch {
            // Local logout should still complete if the network request fails.
        }
    }, []);

    if (state === STATE.CHECKING) {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh'}}>
                <Typography sx={{color: 'var(--cg-text-soft)'}}>Checking access...</Typography>
            </Box>
        );
    }

    if (state === STATE.AUTHED) {
        return typeof children === 'function'
            ? children({user: currentUser, onLogout: handleLogout})
            : children;
    }

    const isSetup = state === STATE.SETUP;
    const title = isSetup ? 'Create first user' : 'ContentLab login';
    const helperText = isSetup
        ? 'Set up the first account for this store.'
        : 'Sign in with your username and password.';
    // Only surface the picker when more than one store exists and the
    // URL hasn't already pinned one.
    const showStorePicker = !storeLockedByPath && stores.length > 1;

    return (
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '24px'}}>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    padding: '28px',
                    borderRadius: '14px',
                    border: '1px solid var(--cg-border)',
                    background: 'var(--cg-surface)',
                }}>
                <Stack gap="18px">
                    <Box>
                        <Typography sx={{fontSize: 18, fontWeight: 700, color: 'var(--cg-text)'}}>
                            {title}
                        </Typography>
                        <Typography sx={{fontSize: 13, color: 'var(--cg-text-soft)', marginTop: '4px'}}>
                            {helperText}
                        </Typography>
                    </Box>
                    {showStorePicker && (
                        <TextField
                            select
                            label="Store"
                            value={selectedStore}
                            onChange={handleStoreChange}
                            size="small"
                            fullWidth
                            disabled={submitting}>
                            {stores.map((store) => (
                                <MenuItem key={store.store} value={store.store}>
                                    {store.label || store.store}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        label="Username"
                        value={credentials.username}
                        onChange={updateCredential('username')}
                        autoFocus
                        autoComplete="username"
                        size="small"
                        fullWidth
                        disabled={submitting}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        value={credentials.password}
                        onChange={updateCredential('password')}
                        autoComplete={isSetup ? 'new-password' : 'current-password'}
                        size="small"
                        fullWidth
                        disabled={submitting}
                    />
                    {error && <Alert severity="error" sx={{fontSize: 12}}>{error}</Alert>}
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting || !credentials.username.trim() || !credentials.password}>
                        {submitting
                            ? isSetup ? 'Creating...' : 'Signing in...'
                            : isSetup ? 'Create account' : 'Sign in'}
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
